import { serializePaymentPlan } from '#/api/srm/serializePaymentPlan';
import { resolveUnitId } from '#/modules/imsMgmt/helper/materialUnit/resolveUnitId';
import { ROUNDING_MODE, resolveRoundingMode } from '#/utils/roundingMode';

function toNumberOrNull(value) {
  if (value === '' || value == null) {
    return null;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/** BE `amount_summary.tax_rate` 為 fraction（0.05）；表單預覽用百分比。 */
function taxRateToFormPercent(taxRate) {
  if (taxRate == null || taxRate === '') {
    return 5;
  }
  const n = Number(taxRate);
  if (!Number.isFinite(n)) {
    return 5;
  }
  if (n > 0 && n <= 1) {
    return Math.round(n * 10000) / 100;
  }
  return n;
}

function toCancelQty(value) {
  if (value === '' || value == null) {
    return 0;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function normalizeTaxType(taxType) {
  if (taxType === 'TAXABLE') {
    return 'TAX_EXCLUDED';
  }
  return taxType || 'TAX_EXCLUDED';
}

function resolveAmendmentLineOrderQty(line) {
  const fromSummary = line?.qtySummary?.orderQty;
  if (fromSummary !== '' && fromSummary != null) {
    return fromSummary;
  }
  return line?.orderQty ?? '';
}

function resolveAmendmentLineCancelQty(line) {
  const fromSummary = line?.qtySummary?.cancelQty;
  if (fromSummary !== '' && fromSummary != null) {
    return fromSummary;
  }
  return line?.cancelQty ?? 0;
}

function resolveAmendmentLineUnitId(line) {
  return resolveUnitId(line?.unit)
    || String(line?.unitId ?? '').trim()
    || '';
}

/**
 * detail → 異動表單 defaultValues（預填稅別／事由／明細；不帶 supplier／currency／到貨日）。
 * Header 稅別／稅率／rounding 只讀 `amountSummary`；數量自 `qtySummary` 讀。
 */
export function mapPurchaseOrderDetailToAmendmentFormValues(detail) {
  const amountSummary = detail?.amountSummary;
  return {
    taxType: normalizeTaxType(amountSummary?.taxType),
    taxRate: taxRateToFormPercent(amountSummary?.taxRate),
    roundingMode: resolveRoundingMode(
      detail?.roundingMode ?? amountSummary?.roundingMode,
      ROUNDING_MODE.HALF_UP,
    ),
    issueReason: detail?.issueReason ?? '',
    remark: detail?.remark ?? '',
    lines: (detail?.lines ?? []).map((line) => {
      const unitId = resolveAmendmentLineUnitId(line);
      return {
        mtrId: line.mtr?.id ?? line.mtrId ?? '',
        mtrNo: line.mtr?.mtrNo ?? '',
        mtrName: line.mtr?.name ?? '',
        unitId,
        orderQty: resolveAmendmentLineOrderQty(line),
        cancelQty: resolveAmendmentLineCancelQty(line),
        unitPrice: line.unitPrice ?? '',
        remark: line.remark ?? '',
      };
    }),
  };
}

/**
 * 既有異動草稿 → 表單值：讀 `after` 快照（PurchaseOrderMutationRequest：`materialId`／`orderQuantity`…），
 * 物料名稱自採購單明細對照（草稿新增、原單沒有的物料只顯示 id 尾碼）。
 */
export function mapPurchaseOrderAmendmentToFormValues(amendment, detail) {
  const after = amendment?.after ?? {};
  const mtrLookup = new Map(
    (detail?.lines ?? []).map((line) => [line.mtr?.id ?? line.mtrId, line]),
  );
  return {
    taxType: normalizeTaxType(after.taxType ?? detail?.amountSummary?.taxType),
    taxRate: taxRateToFormPercent(after.taxRate ?? detail?.amountSummary?.taxRate),
    roundingMode: resolveRoundingMode(after.roundingMode, ROUNDING_MODE.HALF_UP),
    issueReason: amendment?.issueReason ?? '',
    remark: amendment?.remark ?? '',
    lines: (after.lines ?? []).map((line) => {
      const mtrId = line.materialId ?? line.mtrId ?? '';
      const known = mtrLookup.get(mtrId);
      return {
        mtrId,
        mtrNo: known?.mtr?.mtrNo ?? `…${String(mtrId).slice(-6)}`,
        mtrName: known?.mtr?.name ?? '',
        unitId: line.inputUnitId ?? line.unitId ?? resolveAmendmentLineUnitId(known) ?? '',
        orderQty: line.orderQuantity ?? line.orderQty ?? '',
        cancelQty: line.cancelQuantity ?? line.cancelQty ?? 0,
        unitPrice: line.unitPrice ?? '',
        remark: line.remark ?? '',
      };
    }),
  };
}

/**
 * 異動表單 → amendment body（camel；API 層 keysToSnake）。
 * Line：`mtrId`、可選 `unitId`、`qtySummary.{orderQty,cancelQty}`、`unitPrice`、`remark`。
 * `paymentPlan` 選填：有期別才送（總額改變時後端要求必附）。
 * 禁送：派生金額、扁平 `orderQty`／`cancelQty`、supplier／currency／rate／expectedArrivalDate。
 */
export function buildPurchaseOrderAmendmentPayload(values, { updatedAt } = {}) {
  const taxType = normalizeTaxType(values.taxType);

  const lines = (values.lines ?? []).map((line) => {
    const row = {
      mtrId: line.mtrId,
      qtySummary: {
        orderQty: Number(line.orderQty),
        cancelQty: toCancelQty(line.cancelQty),
      },
      unitPrice: toNumberOrNull(line.unitPrice),
      remark: line.remark || null,
    };

    const unitId = String(line.unitId ?? '').trim();
    if (unitId) {
      row.unitId = unitId;
    }

    return row;
  });

  const body = {
    taxType,
    roundingMode: resolveRoundingMode(values.roundingMode, ROUNDING_MODE.HALF_UP),
    issueReason: values.issueReason || null,
    remark: values.remark || null,
    updatedAt: updatedAt || null,
    lines,
  };
  const paymentPlan = serializePaymentPlan(values.paymentPlan);
  if (paymentPlan) {
    body.paymentPlan = paymentPlan;
  }
  return body;
}
